// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Third party imports
import { cloneDeep } from 'lodash';
import { DxDataGridComponent, DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// Constant imports
import { ActionConstants } from '../../../../_constants/action-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';

// Service imports
import { ContextManagerService } from '../../../../_services/data-services/context-manager.service';
import { UserService } from '../../../../_services/data-services/user.service';
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { InvoiceDetailsService } from '../../_services/account-receivable/invoice-details.service';
import { CirabooksHelperService } from '../../../_services/cirabooks-helper.service';

// Model imports
import { PropertyCommunityInfoResultDto } from '../../../../_models/property-community-info-result-dto';
import { ARInvoiceDetailParameterDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-detail-parameter-dto';
import { ARInvoiceEditorResultDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-editor-result-dto';
import { ARInvoiceItemRawDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-item-raw-dto';
import { ARInvoiceRawDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-raw-dto';
import { ARInvoiceSubcategoriesParameterDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-subcategories-parameter-dto';
import { DimensionOptionCodeRawDto } from '../../_models/common/dimensions/dimension-option-code-raw-dto';
import { DimensionSelectorResultDto } from '../../_models/common/dimensions/dimension-selector-result-dto';
import { GLAccountLabelSelectorResultDto } from '../../_models/common/gl-account/gl-account-label-selector-result-dto';
import { GLAccountSelectorResultDto } from '../../_models/common/gl-account/glaccount-selector-result-dto';
import { GLSubcategorySelectorResultDto } from '../../_models/common/gl-account/glsubcategory-selector-result-dto';
import { TransactionLogParameterDto } from '../../_models/common/transaction-log-parameter-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { ARInvoiceGLAccountLabelsParameterDto } from '../../_models/account-receivable/ar-invoice/ar-invoice-gl-account-labels-parameter-dto';

@Component({
    selector: 'app-ar-invoice-editor',
    templateUrl: './ar-invoice-editor.component.html',
    styleUrls: ['./ar-invoice-editor.component.scss']
})
export class ARInvoiceEditorComponent implements OnInit {
    @ViewChild('arInvoiceMessageBox', { static: false }) arInvoiceMessageBox: MessageBoxComponent;
    @ViewChild('arInvoiceEditorPopup', { static: false }) arInvoiceEditorPopup: DxPopupComponent;
    @ViewChild('arInvoiceEditorGrid', { static: false }) arInvoiceEditorGrid: DxDataGridComponent;

    @Input() visible: boolean;
    @Input() popUpTitle: string;
    @Input() invoiceEditorParam: ARInvoiceDetailParameterDto;
    @Output() onInvoiceEditorClose = new EventEmitter<boolean>();

    loadingVisible: boolean;

    currencyFormat: any;
    dateFormat: any;

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;
    historyIcon = IconConstants.HISTORY;
    iconDelete = IconConstants.DELETE;

    invoiceEditorData: ARInvoiceEditorResultDto;
    invoiceInfo: ARInvoiceRawDto = new ARInvoiceRawDto();
    invoiceId: any;

    modifiedInvoiceInfo: ARInvoiceRawDto;
    originalInvoice: ARInvoiceRawDto;
    dimensionSource: DimensionSelectorResultDto[] = [];
    glAccountNumberSource: GLAccountSelectorResultDto[] = [];
    glAccountLabelSource: GLAccountLabelSelectorResultDto[] = [];
    defaultOffsetGLAccountNumber: string;
    selectedAccountType: string;

    accountTypeSource: Array<any> = [];

    entityUsesSubCategory: boolean;
    hasOtherDimensions: boolean;
    allowFullEdit: boolean;
    allowPartialEdit: boolean;
    allowOtherDimensionEdit: boolean;
    allowAddDeleteLineItems: boolean;

    displayTransactionLogPopUp = false;
    transactionLogTitle: string;
    transactionLogResultData: TransactionLogResultDto[] = [];
    communityId: number;
    isInternalUser = false;

    addLineItemButtonOptions = '';

    constructor(
        private readonly precisionService: PrecisionService,
        private readonly serverErrorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService,
        private readonly invoiceDetailsService: InvoiceDetailsService,
        private readonly userService: UserService,
        private readonly helperService: HelperService,
        private readonly cirabooksHelperService: CirabooksHelperService
    ) {
        this.onNewLineItemClick = this.onNewLineItemClick.bind(this);
    }

    ngOnInit() {
        this.addLineItemButtonOptions = JSON.stringify({
            beforeIcons: [IconConstants.ADD],
            label: 'Add'
        });

        this.dateFormat = StringFormatConstants.dateFormat;
        this.isInternalUser = this.userService.isInternal();

        this.currencyFormat = this.precisionService.getCurrencyFormat();

        if (Utils.notNullAndDefined(this.invoiceEditorParam)) {
            this.populateARInvoiceEditorData();
            this.invoiceId = this.invoiceEditorParam.invoiceId;
        }

        this.configureAccountTypeSource();
    }

    onCustomerNameClick(): void {
        const context: PropertyCommunityInfoResultDto = this.contextManagerService.getSelectedContext();
        if (!!context) {
            this.helperService.openDefaultPropertyPageInNewWindow(context.communityId, context.customerId);
        } else {
            this.helperService.openDefaultPropertyPageInNewWindow(
                this.invoiceEditorParam.communityId,
                this.invoiceInfo.customerCode
            );
        }
    }

    onShown(): void {
        this.arInvoiceEditorPopup.instance.repaint();
        if (!!this.arInvoiceEditorGrid && !!this.arInvoiceEditorGrid.instance) {
            this.arInvoiceEditorGrid.instance.repaint();
        }
    }

    onHidden(): void {
        this.visible = false;
        this.onInvoiceEditorClose.emit(false);
    }

    onTransactionLogClick(): void {
        this.communityId = this.invoiceEditorParam.communityId;
        if (!!this.invoiceEditorParam.invoiceId) {
            this.transactionLogTitle = 'Log of AR Invoice ' + this.invoiceEditorParam.invoiceId;
            this.populateTransactionLog();
            this.displayTransactionLogPopUp = true;
        }
    }

    downloadSupportDocument(): void {
        if (
            Utils.notNullAndDefined(this.invoiceEditorData) &&
            Utils.notNullAndDefined(this.invoiceEditorData.supportDocument)
        ) {
            this.helperService.downloadFileUsingUrl(
                this.invoiceEditorData.supportDocument.downloadUrl,
                this.invoiceEditorData.supportDocument.fileName
            );
        }
    }

    onEditorContentReady(event): void {
        event.component.filter(['crudAction', '<>', ActionConstants.DELETE]);
    }

    onRowUpdated(event): void {
        const rowData = event.data;
        rowData.crudAction = Utils.notNullOrEmpty(rowData.crudAction) ? rowData.crudAction : ActionConstants.EDIT;
        const dimensions: DimensionOptionCodeRawDto[] = [];
        if (Utils.isTrue(this.allowFullEdit)) {
            rowData.openBalance = rowData.amount;
        }
        this.invoiceInfo.items.forEach((item) => {
            if (item.arInvoiceItemId === rowData.arInvoiceItemId) {
                this.dimensionSource.forEach((row) => {
                    const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                    obj.dimensionCode = row.code;
                    obj.dimensionOptionCode = rowData[row.code];
                    dimensions.push(obj);
                });
                item.dimensions = dimensions;
            }
        });
    }

    onNewLineItemClick(): void {
        this.addRawLineItem();
    }

    onAccountSelection(event, cellInfo) {
        this.selectedAccountType = event.accountType;
        cellInfo.setValue(event.title);
        cellInfo.data.accountLabelCode = event.accountLabelCode;
        cellInfo.data.glAccountNumber = event.glAccountNumber;
        cellInfo.data.offsetGLAccountNumber = event.offsetGLAccountNumber;
        cellInfo.data.disableSubCategory = event.disableSubCategory;
        cellInfo.data.subCategoryCode = event.subCategoryCode;
        cellInfo.data.tab = event.accountType;

        if (this.entityUsesSubCategory && event.glAccountNumber) {
            this.populateSubcategories(event.glAccountNumber, cellInfo.data, null, event.accountType, false, true);
        }
    }

    onSaveClick(): void {
        this.arInvoiceEditorGrid.instance.saveEditData().then(() => {
            this.saveInvoice();
        });
    }

    saveInvoice(): void {
        const result = { isValid: true, message: null };
        const isLineItemPrepared = this.prepareLineItems();
        if (!isLineItemPrepared) {
            result.isValid = false;
            result.message = 'Line Item Amount must be different from zero';
        }
        if (result.isValid) {
            const lineItemValidity = this.checkLineItemValidity();
            if (Utils.isTrue(lineItemValidity.isValid)) {
                // In case of DELETE crud Action following code is required to remove null dimensionOptionCode
                this.modifiedInvoiceInfo.items.forEach((item) => {
                    item.dimensions = item.dimensions.filter((d) => Utils.notNullOrEmpty(d.dimensionOptionCode));
                });
                this.editARInvoice();
            } else {
                this.arInvoiceMessageBox.showErrorMessage('Error', lineItemValidity.message);
            }
        } else {
            this.arInvoiceMessageBox.showErrorMessage('Error', result.message);
        }
    }

    onSubLedgerChanged(event): void {
        if (!!event && !!event.value) {
            this.populateGLAccountsLabel(event.value);
        }
    }

    onDeleteFromGrid(record: ARInvoiceItemRawDto): void {
        if (Utils.notEqualsIgnoreCase(record.crudAction, ActionConstants.ADD)) {
            record.crudAction = ActionConstants.DELETE;
        } else {
            this.invoiceInfo.items = this.invoiceInfo.items.filter((d) => Utils.notEquals(d.itemId, record.itemId));
        }
        this.arInvoiceEditorGrid.instance.filter(['crudAction', '<>', ActionConstants.DELETE]);
        this.arInvoiceEditorGrid.instance.refresh();
    }

    private getSubCategories(): void {
        if (this.invoiceInfo.items && this.invoiceInfo.items.length > 0) {
            this.invoiceInfo.items.forEach((item) => {
                const accountType = Utils.notNullOrEmpty(item.accountLabelCode) ? 'account_label' : 'account_number';
                if (item.glAccountNumber) {
                    this.populateSubcategories(
                        item.glAccountNumber,
                        item,
                        item.subCategoryCode,
                        accountType,
                        false,
                        true
                    );
                }
            });
        }
    }

    private populateSubcategories(
        glAccountNumber: any,
        rawItem: any,
        selectedSubcategoryCode: string = null,
        selectedAccountType,
        clearCache = false,
        showInformation: boolean
    ): void {
        const baseParam: ARInvoiceSubcategoriesParameterDto = new ARInvoiceSubcategoriesParameterDto();
        baseParam.managementCompanyId = this.invoiceEditorParam.managementCompanyId;
        baseParam.communityId = this.invoiceEditorParam.communityId;
        baseParam.glAccountNumber = glAccountNumber;
        baseParam.clearCache = clearCache;

        this.loadingVisible = true;
        this.invoiceDetailsService.getARInvoiceSubcategories(baseParam).subscribe(
            (result: GLSubcategorySelectorResultDto[]) => {
                if (Utils.isTrue(this.entityUsesSubCategory)) {
                    const { rawItem: updatedRawItem, infoMessage } = this.cirabooksHelperService.processSubcategories(
                        rawItem,
                        result,
                        selectedSubcategoryCode,
                        selectedAccountType,
                        this.glAccountLabelSource,
                        this.glAccountNumberSource
                    );

                    // Replace the original rawItem with the updated one
                    rawItem = updatedRawItem;

                    if (Utils.isTrue(showInformation) && Utils.notNullOrEmpty(infoMessage)) {
                        const dialog = this.helperService.getOkDialog('Information', infoMessage);
                        dialog.show();
                    }
                }

                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateTransactionLog(): void {
        const baseParam: TransactionLogParameterDto = new TransactionLogParameterDto();
        baseParam.transactionId = this.invoiceEditorParam.invoiceId;
        baseParam.clearCache = true;
        this.loadingVisible = true;
        this.invoiceDetailsService.getInvoiceHistory(baseParam).subscribe(
            (result: TransactionLogResultDto[]) => {
                this.transactionLogResultData = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateARInvoiceEditorData(): void {
        this.loadingVisible = true;
        this.invoiceDetailsService.getARInvoiceEditor(this.invoiceEditorParam).subscribe(
            (result: ARInvoiceEditorResultDto) => {
                this.invoiceEditorData = result;
                this.invoiceInfo = result.invoiceInfo;
                this.dimensionSource = result.glDimensions;
                this.glAccountNumberSource = result.glAccounts;
                this.glAccountLabelSource = result.glAccountLabels;
                this.defaultOffsetGLAccountNumber = result.defaultOffsetGLAccountNumber;
                this.entityUsesSubCategory = result.entityUsesSubCategory;
                this.allowFullEdit = result.allowFullEdit;
                this.allowPartialEdit = result.allowPartialEdit;
                this.allowOtherDimensionEdit = result.allowOtherDimensionEdit;
                this.allowAddDeleteLineItems = result.allowAddDeleteLineItems;

                if (Utils.isNullOrEmpty(this.invoiceInfo.items)) {
                    this.addRawLineItem();
                } else {
                    this.prepareEditorLineItems();
                }

                if (this.entityUsesSubCategory) {
                    this.getSubCategories();
                }

                this.originalInvoice = cloneDeep(this.invoiceEditorData.invoiceInfo);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.onHidden();
                this.loadingVisible = false;
            }
        );
    }

    private populateGLAccountsLabel(subLedgerId: number): void {
        this.glAccountLabelSource = [];
        const param: ARInvoiceGLAccountLabelsParameterDto = new ARInvoiceGLAccountLabelsParameterDto();
        param.communityId = this.invoiceEditorParam.communityId;
        param.managementCompanyId = this.invoiceEditorParam.managementCompanyId;
        param.subLedgerId = subLedgerId;
        this.invoiceDetailsService.getARInvoiceGLAccountLabels(param).subscribe(
            (result) => {
                this.glAccountLabelSource = result;
                this.prepareEditorLineItems();
            },
            (error) => {
                this.serverErrorService.displayError(error);
            }
        );
    }

    private configureAccountTypeSource(): void {
        this.accountTypeSource = [
            {
                name: 'account_label',
                text: 'Account Label'
            },
            {
                name: 'account_number',
                text: 'Account Number'
            }
        ];
    }

    private prepareEditorLineItems(): void {
        const arInvoiceItems: ARInvoiceItemRawDto[] = [];
        let index = 0;
        this.invoiceInfo.items.forEach((item) => {
            index += 1;
            let obj: ARInvoiceItemRawDto = new ARInvoiceItemRawDto();
            obj = Object.assign(obj, item);
            obj['itemId'] = index;

            const selectedGLAccount = this.glAccountNumberSource.find(
                (d) => d.glAccountNumber === item.glAccountNumber
            );
            if (!!selectedGLAccount) {
                obj.glAccountNumber = selectedGLAccount.glAccountNumber;
                obj['selectedAccountName'] = `${selectedGLAccount.title};${selectedGLAccount.glAccountNumber}`;
                this.populateSubcategories(
                    selectedGLAccount.glAccountNumber,
                    obj,
                    item.subCategoryCode,
                    'account_number',
                    false,
                    true
                );
            }

            const selectedOffsetGLAccount = this.glAccountNumberSource.find(
                (d) => d.glAccountNumber === item.offsetGLAccountNumber
            );
            if (!!selectedOffsetGLAccount) {
                obj.offsetGLAccountNumber = selectedOffsetGLAccount.glAccountNumber;
            }

            const selectedGLAccountLabel = this.glAccountLabelSource.find(
                (d) => !!item.accountLabelCode && d.code.toLowerCase() === item.accountLabelCode.toLowerCase()
            );
            if (!!selectedGLAccountLabel) {
                obj.accountLabelCode = selectedGLAccountLabel.code;
                obj[
                    'selectedAccountName'
                ] = `${selectedGLAccountLabel.title};${selectedGLAccountLabel.glAccountNumber}`;
                if (!!selectedGLAccountLabel.subCategoryCode) {
                    obj.subCategoryCode = selectedGLAccountLabel.subCategoryCode;
                    obj['disableSubCategory'] = true;
                    this.populateSubcategories(
                        selectedGLAccountLabel.glAccountNumber,
                        obj,
                        item.subCategoryCode,
                        'account_label',
                        false,
                        true
                    );
                }
            }

            arInvoiceItems.push(obj);
        });

        this.invoiceInfo.items = arInvoiceItems;
        this.prepareDimensions();
    }

    private addRawLineItem(): void {
        const item: ARInvoiceItemRawDto = new ARInvoiceItemRawDto();
        item.amount = 0;
        item.arInvoiceItemId = null;
        item['itemId'] = Utils.notNullOrEmpty(this.invoiceInfo.items)
            ? Math.max.apply(
                  Math,
                  this.invoiceInfo.items.map((o) => {
                      return o['itemId'];
                  })
              ) + 1
            : 1;
        item.crudAction = ActionConstants.ADD;
        item.arInvoiceId = this.invoiceInfo.invoiceId;
        item.description = this.invoiceInfo.description;

        item['selectedAccountName'] = '';
        item['disableSubCategory'] = false;
        item['subCategorySource'] = [];
        item['accountType'] = '';

        this.invoiceInfo.items.push(item);

        this.prepareDimensions();
    }

    private prepareDimensions(): void {
        if (this.invoiceInfo.items && this.invoiceInfo.items.length > 0) {
            this.invoiceInfo.items.forEach((item) => {
                const dimensions: DimensionOptionCodeRawDto[] = [];
                this.dimensionSource.forEach((dimNode) => {
                    let itemDimension: DimensionOptionCodeRawDto;

                    if (item.dimensions && item.dimensions.length > 0) {
                        itemDimension = item.dimensions.find(
                            (d) => d.dimensionCode.toLowerCase() === dimNode.code.toLowerCase()
                        );
                    }
                    if (itemDimension) {
                        if (itemDimension.dimensionOptionCode) {
                            const selectedNode = dimNode.options.find(
                                (d) => d.code.toLowerCase() === itemDimension.dimensionOptionCode.toLowerCase()
                            );
                            item[itemDimension.dimensionCode] = selectedNode ? selectedNode.code : null;
                        } else if (!item[itemDimension.dimensionCode]) {
                            item[itemDimension.dimensionCode] = null;
                        }
                    } else {
                        const obj: DimensionOptionCodeRawDto = new DimensionOptionCodeRawDto();
                        obj.dimensionCode = dimNode.code;
                        obj.dimensionOptionCode = null;
                        item[dimNode.code] = null;
                        if (Utils.isNullOrUndefined(item.dimensions)) {
                            item.dimensions = [];
                        }
                        item.dimensions.push(obj);
                    }
                });
            });
        }
    }

    private prepareLineItems(): boolean {
        let isLineItemPrepared = true;
        const modifiedLineItems: Array<ARInvoiceItemRawDto> = [];
        this.modifiedInvoiceInfo = new ARInvoiceRawDto();

        if (this.invoiceInfo.items && this.invoiceInfo.items.length > 0) {
            for (const arInvoiceLineItem of this.invoiceInfo.items) {
                const dimensionKeysToDelete: string[] = [];
                const dimensionToDelete: any[] = [];
                let rawItem = Object.assign(new ARInvoiceItemRawDto(), arInvoiceLineItem);
                if (Utils.equalsIgnoreCase(arInvoiceLineItem.crudAction, ActionConstants.DELETE)) {
                    rawItem = this.originalInvoice.items.find((i) =>
                        Utils.equals(i.arInvoiceItemId, arInvoiceLineItem.arInvoiceItemId)
                    );
                    rawItem.crudAction = ActionConstants.DELETE;
                } else {
                    // As per DEV-3131, AR Invoice editor should accepts -ve amount
                    if (Utils.notEquals(rawItem.amount, 0)) {
                        rawItem.crudAction = !!rawItem.crudAction ? rawItem.crudAction : '';
                        if (Utils.notNullOrEmpty(rawItem.dimensions)) {
                            rawItem.dimensions.forEach((dim) => {
                                const isDimRequired = this.isDimensionMandatory(dim);
                                dim.dimensionOptionCode = rawItem[dim.dimensionCode];
                                dimensionKeysToDelete.push(dim.dimensionCode);

                                if (!isDimRequired && !dim.dimensionOptionCode) {
                                    dimensionToDelete.push(dim.dimensionCode);
                                }
                            });
                            dimensionToDelete.forEach((dim) => {
                                const rawIndex = rawItem.dimensions.findIndex((d) =>
                                    Utils.equalsIgnoreCase(d.dimensionCode, dim)
                                );
                                rawItem.dimensions.splice(rawIndex);
                            });
                        }
                        dimensionKeysToDelete.forEach((key) => {
                            delete rawItem[key];
                        });
                        delete rawItem['subCategorySource'];
                        delete rawItem['selectedAccountName'];
                        delete rawItem['disableSubCategory'];
                        delete rawItem['accountType'];
                        delete rawItem['itemId'];
                    } else {
                        isLineItemPrepared = false;
                        break;
                    }
                }
                modifiedLineItems.push(rawItem);
            }
        }
        if (!!isLineItemPrepared) {
            this.modifiedInvoiceInfo = Object.assign(this.modifiedInvoiceInfo, this.invoiceInfo);
            this.modifiedInvoiceInfo.items = modifiedLineItems;
        } else {
            this.resetARInvoiceRawItem();
        }

        return isLineItemPrepared;
    }

    private isDimensionMandatory(dimensionToCheck: DimensionOptionCodeRawDto): boolean {
        const dimension = this.dimensionSource.find((d) =>
            Utils.equalsIgnoreCase(d.code.toLowerCase(), dimensionToCheck.dimensionCode.toLowerCase())
        );
        return Utils.notNullAndDefined(dimension) ? dimension.required : false;
    }

    private resetARInvoiceRawItem(): void {
        this.prepareDimensions();
        if (this.entityUsesSubCategory) {
            this.getSubCategories();
        }
    }

    private editARInvoice(): void {
        this.loadingVisible = true;
        this.invoiceDetailsService.updateARInvoice(this.modifiedInvoiceInfo).subscribe(
            () => {
                const message = 'AR Invoice updated successfully.';
                notify(message, 'Success', MessageInterval.successMsgInterval);
                this.visible = false;
                this.onInvoiceEditorClose.emit(true);
                this.loadingVisible = false;
            },
            (error) => {
                this.serverErrorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private checkLineItemValidity(): any {
        const resultStatus = { isValid: true, message: null };

        this.invoiceInfo.items.forEach((data, index) => {
            if (Utils.notEqualsIgnoreCase(data.crudAction, ActionConstants.DELETE)) {
                const itemIndex = index + 1;
                if (Utils.isNullOrUndefined(data.glAccountNumber)) {
                    resultStatus.isValid = false;
                    resultStatus.message = `The GL Account number is Required for ItemId #${itemIndex}`;
                } else if (
                    Utils.isFalse(data.isAccountSubcategoryValid) &&
                    Utils.notNullAndDefined(data.accountSubcategoryValidationMessage)
                ) {
                    resultStatus.isValid = false;
                    resultStatus.message = `Invalid Line Item as ${data.accountSubcategoryValidationMessage}`;
                } else if (Utils.equals(data.amount, 0)) {
                    resultStatus.isValid = false;
                    resultStatus.message = `Line Item Amount must not equal to Zero ItemId #${itemIndex}`;
                }

                this.dimensionSource.forEach((dimension) => {
                    if (Utils.isTrue(dimension.required)) {
                        const itemDimension = data.dimensions.find((d) =>
                            Utils.equalsIgnoreCase(d.dimensionCode, dimension.code)
                        );
                        if (
                            Utils.notNullAndDefined(itemDimension) &&
                            Utils.isNullOrUndefined(itemDimension.dimensionOptionCode)
                        ) {
                            resultStatus.isValid = false;
                            resultStatus.message = `${dimension.code} is Required for ItemId #${itemIndex}`;
                        }
                    }
                });
            }
        });
        return resultStatus;
    }
}
